import { SeverityBadge } from './SeverityBadge';
import { ProductTabs } from './ProductTabs';
import type { Problem, ProductRecommendations, Confidence } from '../../types/diagnosis';

interface DiagnosisResultProps {
  isHealthy: boolean;
  problems: Problem[];
  products: ProductRecommendations | null;
  generalTips: string | null;
  photoURL: string;
  showPhoto?: boolean;
  className?: string;
}

/**
 * Editorial health report — magazine-style diagnosis display
 * Matches the 06-diagnosis mockup with numbered symptoms, timeline treatment, and prevention blockquote
 */
export function DiagnosisResult({
  isHealthy,
  problems,
  products,
  generalTips,
  photoURL,
  showPhoto = true,
  className = '',
}: DiagnosisResultProps) {
  return (
    <div className={`space-y-0 ${className}`}>
      {/* Hero photo */}
      {showPhoto && photoURL && (
        <div className="relative w-full overflow-hidden rounded-lg shadow-sm mb-6">
          <img
            src={photoURL}
            alt="Diagnosed plant"
            className="w-full h-[280px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          {!isHealthy && problems[0] && (
            <div className="absolute bottom-4 left-4">
              <SeverityBadge severity={problems[0].severity} />
            </div>
          )}
        </div>
      )}

      {/* Healthy result */}
      {isHealthy ? (
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-status-green/10 mb-4">
            <span
              className="material-symbols-outlined text-status-green"
              style={{ fontSize: 32 }}
              aria-hidden="true"
            >
              check_circle
            </span>
          </div>
          <h2 className="text-3xl font-display font-bold text-editorial-black mb-2">
            Looking Healthy!
          </h2>
          <p className="font-serif italic text-gray-500 text-lg">
            No issues detected
          </p>
          {generalTips && (
            <>
              <div className="h-px w-full bg-gray-200 my-6" />
              <section className="px-2 text-left">
                <h4 className="section-header mb-4">Care Tips</h4>
                <div className="relative pl-6 border-l-4 border-editorial-gold">
                  <p className="text-lg font-serif italic text-editorial-black/90 leading-relaxed">
                    &ldquo;{generalTips}&rdquo;
                  </p>
                </div>
              </section>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Problem sections */}
          {problems.map((problem, problemIndex) => (
            <ProblemSection
              key={problemIndex}
              problem={problem}
              isFirst={problemIndex === 0}
            />
          ))}

          {/* Product recommendations */}
          {products && (
            <>
              <div className="h-px w-full bg-gray-200 my-2" />
              <section className="px-2 py-6">
                <h4 className="section-header mb-6">Helpful Products</h4>
                <ProductTabs
                  products={products}
                  problemNames={problems.map((p) => p.problem)}
                />
                <p className="text-[10px] font-sans text-gray-400 italic mt-4">
                  Product suggestions are examples only. Availability and effectiveness may vary.
                </p>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}

interface ProblemSectionProps {
  problem: Problem;
  isFirst: boolean;
}

function ProblemSection({ problem, isFirst }: ProblemSectionProps) {
  return (
    <>
      {/* Title */}
      {isFirst && (
        <div className="text-center space-y-2 mb-2">
          <h2 className="text-3xl font-display font-bold leading-tight text-editorial-black">
            {problem.problem}
          </h2>
          <ConfidenceText confidence={problem.confidence} />
        </div>
      )}

      {!isFirst && (
        <>
          <div className="h-px w-full bg-gray-200 my-2" />
          <div className="text-center space-y-2 pt-4 mb-2">
            <h2 className="text-2xl font-display font-bold leading-tight text-editorial-black">
              {problem.problem}
            </h2>
            <ConfidenceText confidence={problem.confidence} />
          </div>
        </>
      )}

      <div className="h-px w-full bg-gray-200 my-2" />

      {/* Symptoms Detected */}
      <section className="px-2 py-6">
        <h4 className="section-header mb-6">Symptoms Detected</h4>
        <div className="space-y-5">
          {problem.symptomsDetected.map((symptom, index) => (
            <div key={index} className="flex items-start gap-4">
              <span className="text-2xl font-serif italic text-editorial-gold/80 leading-none mt-0.5">
                {String(index + 1).padStart(2, '0')}
              </span>
              <p className="text-base font-serif text-editorial-black/80 leading-relaxed">
                {symptom}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="h-px w-full bg-gray-200 my-2" />

      {/* Treatment Plan */}
      <section className="px-2 py-8 bg-white/40">
        <h4 className="section-header mb-8">Treatment Plan</h4>
        <div className="relative border-l border-gray-300 ml-2 pl-8 space-y-10 pb-2">
          {/* Immediate */}
          <div className="relative">
            <div className="absolute -left-[37px] top-1 h-4 w-4 rounded-full bg-status-green border-4 border-background" />
            <h3 className="text-xl font-serif italic font-bold text-editorial-black mb-3">
              Immediate
            </h3>
            <div className="space-y-3">
              {problem.treatment.immediate.split(/\.\s+/).filter(Boolean).map((step, i) => (
                <div key={i} className="flex gap-3 text-base text-editorial-black/80 leading-relaxed">
                  <span
                    className="material-symbols-outlined text-status-green shrink-0 mt-0.5"
                    style={{ fontSize: 18 }}
                    aria-hidden="true"
                  >
                    check_circle
                  </span>
                  <span className="font-serif">{step.endsWith('.') ? step : `${step}.`}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Ongoing */}
          <div className="relative">
            <div className="absolute -left-[37px] top-1 h-4 w-4 rounded-full bg-gray-300 border-4 border-background" />
            <h3 className="text-xl font-serif italic font-bold text-editorial-black mb-3">
              Ongoing
            </h3>
            <div className="space-y-3">
              {problem.treatment.ongoing.split(/\.\s+/).filter(Boolean).map((step, i) => (
                <div key={i} className="flex gap-3 text-base text-editorial-black/80 leading-relaxed">
                  <span
                    className="material-symbols-outlined text-gray-400 shrink-0 mt-0.5"
                    style={{ fontSize: 18 }}
                    aria-hidden="true"
                  >
                    radio_button_unchecked
                  </span>
                  <span className="font-serif">{step.endsWith('.') ? step : `${step}.`}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute -left-[37px] top-1 h-4 w-4 rounded-full bg-gray-300 border-4 border-background" />
            <h3 className="text-xl font-serif italic font-bold text-editorial-black mb-3">
              Timeline
            </h3>
            <p className="text-base font-serif text-editorial-black/80 leading-relaxed">
              {problem.treatment.timeline}
            </p>
          </div>
        </div>
      </section>

      {/* Prevention */}
      <section className="px-2 py-8">
        <h4 className="section-header mb-6">Prevention</h4>
        <div className="relative pl-6 border-l-4 border-editorial-gold">
          <p className="text-lg font-serif italic text-editorial-black/90 leading-relaxed">
            &ldquo;{problem.prevention}&rdquo;
          </p>
        </div>
      </section>
    </>
  );
}

function ConfidenceText({ confidence }: { confidence: Confidence }) {
  const labels: Record<Confidence, string> = {
    high: 'High confidence diagnosis',
    medium: 'Medium confidence diagnosis',
    low: 'Low confidence — expert review suggested',
  };

  return (
    <p className="text-lg font-serif italic text-gray-500">
      {labels[confidence]}
    </p>
  );
}
